import React, { useEffect, useRef, useState } from 'react';
import { formatSpeedValue, parseManualSpeedInput } from './SpeedSlider';

/** Browsers accept playback rates roughly between 1/16x and 16x. */
export const LOCAL_SPEED_MIN = 0.1;
export const LOCAL_SPEED_MAX = 16;

/**
 * Turns typed text into a playback rate: any positive number for local videos (kept within what
 * browsers can play), or the nearest allowed rate when `allowedRates` is given (YouTube).
 * Returns null when the text is not a number.
 */
export const resolveTypedSpeed = (rawValue: string, allowedRates?: number[]): number | null => {
  const parsed = parseManualSpeedInput(rawValue);
  if (parsed === null) return null;

  if (allowedRates && allowedRates.length > 0) {
    return allowedRates.reduce((nearest, rate) => (
      Math.abs(rate - parsed) < Math.abs(nearest - parsed) ? rate : nearest
    ), allowedRates[0]);
  }

  return Math.min(LOCAL_SPEED_MAX, Math.max(LOCAL_SPEED_MIN, Math.round(parsed * 100) / 100));
};

/** Up/down for local videos: the next 0.1x step in that direction, across the whole typed range. */
export const stepLocalSpeed = (currentRate: number, direction: -1 | 1): number => {
  const current = Number.isFinite(currentRate) && currentRate > 0 ? currentRate : 1;
  const epsilon = 0.0001;
  const next = direction > 0
    ? Math.ceil((current + epsilon) * 10) / 10
    : Math.floor((current - epsilon) * 10) / 10;
  return Math.min(LOCAL_SPEED_MAX, Math.max(LOCAL_SPEED_MIN, Math.round(next * 100) / 100));
};

interface SpeedTextInputProps {
  playbackRate: number;
  onCommit: (rate: number) => void;
  /** When set (YouTube), typed values snap to the nearest of these rates. */
  allowedRates?: number[];
  controlHeight?: number;
  isFullscreen?: boolean;
}

/** "Speed [ 0.75 ]x" field for the settings row. Commits on Enter or when the field loses focus. */
const SpeedTextInput: React.FC<SpeedTextInputProps> = ({
  playbackRate,
  onCommit,
  allowedRates,
  controlHeight = 36,
  isFullscreen = false
}) => {
  const displayValue = formatSpeedValue(playbackRate).replace(/x$/, '');
  const [draft, setDraft] = useState(displayValue);
  const [isEditing, setIsEditing] = useState(false);
  const cancelNextCommitRef = useRef(false);

  useEffect(() => {
    if (!isEditing) setDraft(displayValue);
  }, [displayValue, isEditing]);

  const commit = () => {
    if (cancelNextCommitRef.current) {
      // Escape: keep the current speed.
      cancelNextCommitRef.current = false;
      setIsEditing(false);
      setDraft(displayValue);
      return;
    }
    const nextRate = resolveTypedSpeed(draft, allowedRates);
    setIsEditing(false);
    if (nextRate === null) {
      setDraft(displayValue);
      return;
    }
    setDraft(formatSpeedValue(nextRate).replace(/x$/, ''));
    if (Math.abs(nextRate - playbackRate) > 0.0001) onCommit(nextRate);
  };

  const listId = allowedRates ? 'speed-text-input-rates' : undefined;
  const hint = allowedRates
    ? `YouTube speeds: ${allowedRates.map(rate => formatSpeedValue(rate)).join(', ')}`
    : `Type any speed from ${LOCAL_SPEED_MIN}x to ${LOCAL_SPEED_MAX}x`;

  return (
    <label
      className={`speed-text-input ${isFullscreen ? 'speed-text-input-fullscreen' : ''}`}
      style={{ minHeight: `${controlHeight}px` }}
      title={hint}
    >
      <span className="speed-text-input-label">Speed</span>
      <input
        type="text"
        inputMode="decimal"
        value={draft}
        list={listId}
        aria-label={`Playback speed. ${hint}`}
        onFocus={(event) => {
          setIsEditing(true);
          event.currentTarget.select();
        }}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.currentTarget.blur();
          } else if (event.key === 'Escape') {
            cancelNextCommitRef.current = true;
            event.currentTarget.blur();
          }
        }}
      />
      <span className="speed-text-input-suffix" aria-hidden="true">x</span>
      {allowedRates && (
        <datalist id={listId}>
          {allowedRates.map(rate => <option key={rate} value={formatSpeedValue(rate).replace(/x$/, '')} />)}
        </datalist>
      )}
    </label>
  );
};

export default SpeedTextInput;
